import { useState, useCallback } from 'react';

interface CurrencyInputProps {
  value: number;
  onChange: (value: number) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  label?: string;
}

const formatCurrency = (value: number): string => {
  if (value === 0) return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const parseCurrency = (text: string): number => {
  const cleaned = text.replace(/[^0-9.]/g, '');
  return parseFloat(cleaned) || 0;
};

export default function CurrencyInput({
  value,
  onChange,
  placeholder = '0',
  className = '',
  disabled = false,
  label,
}: CurrencyInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [displayValue, setDisplayValue] = useState(value ? formatCurrency(value) : '');

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    setDisplayValue(value ? String(value) : '');
  }, [value]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    const parsed = parseCurrency(displayValue);
    setDisplayValue(parsed ? formatCurrency(parsed) : '');
    onChange(parsed);
  }, [displayValue, onChange]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (isFocused) {
      // Allow only digits and one decimal point while editing
      const cleaned = raw.replace(/[^0-9.]/g, '');
      setDisplayValue(cleaned);
    } else {
      setDisplayValue(raw);
    }
  }, [isFocused]);

  // Sync display when value prop changes externally
  const shown = isFocused ? displayValue : (value ? formatCurrency(value) : '');

  return (
    <div className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
        <input
          type="text"
          inputMode="numeric"
          value={shown}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`pl-7 pr-3 py-2 border border-gray-200 rounded-lg text-sm w-full
            focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300
            disabled:bg-gray-50 disabled:text-gray-400
            ${className}`}
        />
      </div>
    </div>
  );
}
